import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';
import {getMeta, makeDebouncedLoad} from '../helpers';
import {Modal} from "bootstrap";

export default class extends Controller {
    static targets = ['create'];

    initialize() {
        super.initialize();
        this.dndEnd = (event) => this.listenForDndEnd(event);
        this.listenForSelectCreateSuccess = (event) => this._listenForSelectCreateSuccess(event);
        this.selectAllCallback = (event) => this._selectAllCallback(event);
    }

    connect() {
        this.selectElement = this.element.querySelector('.form-select');
        application.element.addEventListener('dashboard-form:success', this.listenForSelectCreateSuccess);

        if (!this.selectElement.tomselect) {
            this.optionsLabel = this.selectElement.dataset.optionsLabel;
            this.optionsValue = this.selectElement.dataset.optionsValue;
            this.optionsUrl = this.selectElement.dataset.optionsUrl;
            this.modelID = this.selectElement.dataset.modelId;
            this.isMultiple = this.selectElement.dataset.optionsMultiple === '1';
            this.page = 1;
            this.additionalQuery = '';
            this.plugins = ['virtual_scroll'];
            this.defaultEntries = {};

            if (this.isMultiple && !this.selectElement.hasAttribute('disabled')) {
                this.plugins.push('remove_button');
            }

            this.select = new TomSelect(this.selectElement, {
                labelField: this.optionsLabel,
                valueField: this.optionsValue,
                searchField: [],
                create: false,
                preload: 'focus',
                highlight: false,
                maxOptions: null,
                loadThrottle: null,
                plugins: this.plugins,
                firstUrl: (query) => {
                    return this.optionsUrl + '?search=' + encodeURIComponent(query) + '&page=' + this.page + '&model_id=' + this.modelID + this.additionalQuery;
                },
                load: makeDebouncedLoad((query, callback) => {
                    fetch(this.select.getUrl(query))
                        .then(response => response.json())
                        .then(json => {
                            if (json.hasMore) {
                                this.page++;
                                const nextUrl = this.optionsUrl + '?search=' + encodeURIComponent(query) + '&page=' + this.page + '&model_id=' + this.modelID + this.additionalQuery;
                                this.select.setNextUrl(query, nextUrl);
                            }

                            for(const entry of json.entries) {
                                this.defaultEntries[entry[this.optionsValue]] = {
                                    [this.optionsLabel]: entry[this.optionsLabel],
                                    [this.optionsValue]: entry[this.optionsValue],
                                };
                            }

                            callback(json.entries);
                        })
                        .catch((e) => {
                            console.error(e);
                            callback();
                        });
                }),
                shouldLoad: () => {
                    return true;
                },
                onType: () => {
                    this.page = 1;
                },
                render: {
                    option: (data) => {
                        return `<div data-action="click->select#listenAdd">` + data[this.optionsLabel] + `</div>`;
                    },
                    item: (data) => {
                        return `<div>` + data[this.optionsLabel] + `</div>`;
                    },
                    no_more_results: () => {
                        return `<div class="text-center">` + getMeta('dashboard-select-no-more-options-description') + `</div>`;
                    },
                    no_results: () => {
                        return `<div class="text-center">` + getMeta('dashboard-select-no-options-description') + `</div>`;
                    },
                    loading_more: () => {
                        return `<div class="text-center">` + getMeta('dashboard-select-loading') + `</div>`;
                    },
                    loading: () => {
                        return `<div class="text-center">` + getMeta('dashboard-select-loading') + `</div>`;
                    },
                },
            });

            this.select.wrapper.dataset.input = this.selectElement.dataset.input;
            this.select.wrapper.dataset.formTarget = this.selectElement.dataset.formTarget;

            if (this.isMultiple) {
                this.element.addEventListener('dnd:end', this.dndEnd);
            }
        }
    }

    listenForDndEnd() {
        const values = [];
        for (const element of this.element.querySelectorAll('.ts-control [data-value]')) {
            if (element.dataset.value) {
                values.push(element.dataset.value);
            }
        }

        this.select.setValue(values);
    }

    listenAdd() {
        if (this.select.control_input.value) {
            this.select.control_input.value = '';
            this.select.clearOptions();
            this.select.addOptions(Object.values(this.defaultEntries));
        }
    }

    async _listenForSelectCreateSuccess(event) {
        if (this.hasCreateTarget && event.detail.source && event.detail.source.dataset.isSelectModal) {
            const modalElement = event.detail.source.closest('.dashboard-modal');

            if (modalElement) {
                const modalObject = Modal.getInstance(modalElement);

                if (modalObject && modalObject.instigator && this.createTarget === modalObject.instigator) {
                    event.preventDefault();

                    await application.closeModals(1);
                    const form = Stimulus.getControllerForElementAndIdentifier(event.detail.source, 'form');

                    if (form) {
                        form.hideLoader();
                    }

                    this.reload();
                }
            }
        }
    }

    reload() {
        this.select.load('');
        this.select.wrapper.classList.add('preloaded');
    }

    selectAll() {
        this.select.load('');
        this.select.wrapper.classList.add('preloaded');
        this.select.on('load', this.selectAllCallback);
    }

    _selectAllCallback() {
        this.select.off('load', this.selectAllCallback);
        this.select.setValue(Object.keys(this.select.options));
    }

    clear() {
        this.select.clear();
    }

    disconnect() {
        application.element.removeEventListener('dashboard-form:success', this.listenForSelectCreateSuccess);
        this.element.removeEventListener('dnd:end', this.dndEnd);
        this.select.destroy();
        super.disconnect();
    }
}
